import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Trip } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconTrips } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { TripDrawer } from '@/components/admin/TripDrawer';

const PAGE = 25;

// Trip status → label + tone (canonical Telride booking_statuses).
const STATUS: Record<number, { label: string; tone: BadgeTone }> = {
  1: { label: 'Requested', tone: 'info' },
  2: { label: 'Arrived', tone: 'info' },
  3: { label: 'Started', tone: 'warn' },
  5: { label: 'Completed', tone: 'ok' },
  6: { label: 'Cancelled', tone: 'danger' },
  7: { label: 'Cancelled (driver)', tone: 'danger' },
};

/** Trips — list/search the trips table (owned by trip-svc). DEFENSIVE: the
 *  table isn't in Postgres yet (still Laravel/MySQL pre-cutover), so the page
 *  shows a clean "not yet available" state driven by the API's available flag.
 *  Covers the Filament TripResource / TripRequestResource list/view. */
export function Trips() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'trips', q, offset],
    queryFn: ({ signal }) => adminApi.trips({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Trip>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'trip_id', header: 'Trip', cell: (r) => <span className="text-slate-200">{r.trip_id || '—'}</span> },
    { key: 'cust', header: 'Customer', cell: (r) => <span className="metric text-xs text-slate-300">#{r.customer_id}</span> },
    { key: 'drv', header: 'Driver', cell: (r) => <span className="metric text-xs text-slate-300">{r.driver_id ? `#${r.driver_id}` : '—'}</span> },
    { key: 'pickup', header: 'Pickup', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.pickup_address}>{r.pickup_address || '—'}</span> },
    { key: 'drop', header: 'Drop', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.drop_address}>{r.drop_address || '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => {
        const s = STATUS[r.status] ?? { label: `#${r.status}`, tone: 'neutral' as BadgeTone };
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    { key: 'total', header: 'Total', align: 'right', cell: (r) => <span className="metric text-xs">{r.total ? `$${r.total.toFixed(2)}` : '—'}</span> },
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Trips"
        subtitle="Ride history — search by trip id or address."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search trips…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Trips not yet in Postgres"
            description={
              data?.note ??
              'The trips table is owned by trip-svc and is still in Laravel/MySQL pre-cutover. This page lights up automatically once the table lands here.'
            }
            icon={<IconTrips className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading trips…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load trips" description={(error as Error)?.message} />
          ) : (
            <>
              <DataTable<Trip>
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(r) => String(r.id)}
                stickyHeader
                empty={<EmptyState title="No trips" description="No trips match this search." icon={<IconTrips className="h-6 w-6" />} />}
              />
              <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
            </>
          )}
        </Card>
      )}

      {selected !== null ? <TripDrawer id={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}
